import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { caaCustomerOrdersQuery } from "../graphql/customer-account-gql";
import { INFINITE_LIST_QUERY_OPTIONS } from "../graphql/queryClient";
import type { ICaaCustomerOrdersQuery } from "../interface/customer-account";
import type { IUseOrdersQueryCaa } from "../types/queries";

/** useInfiniteQuery 페이지들을 단일 응답 형태로 합침 */
function mergeCaaOrderPages(pages: ICaaCustomerOrdersQuery[] | undefined): ICaaCustomerOrdersQuery | undefined {
  if (!pages?.length) return undefined;
  const last = pages[pages.length - 1];
  if (!last.customer?.orders) return last;

  return {
    customer: {
      ...last.customer,
      orders: {
        ...last.customer.orders,
        edges: pages.flatMap((page) => page.customer?.orders?.edges ?? []),
        pageInfo: last.customer.orders.pageInfo,
      },
    },
  } as ICaaCustomerOrdersQuery;
}

/**
 * classic useOrdersQuery의 Customer Account API 대응 — 주문 내역 무한스크롤
 * CAA는 Storefront API와 엔드포인트·인증이 달라 앱이 주입한 실행기(request)를 사용
 */
export default function useOrdersQueryCaa({ request, enabled = true, first = 10 }: IUseOrdersQueryCaa) {
  const variables = { first, sortKey: "PROCESSED_AT", reverse: true };

  // cache-first(staleTime: Infinity)로 목록→상세→뒤로가기 시 페이지/스크롤 복원
  const ordersInfinite = useInfiniteQuery({
    ...INFINITE_LIST_QUERY_OPTIONS,
    queryKey: ["shopifyV2", "caa-orders", variables],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => request<ICaaCustomerOrdersQuery>(caaCustomerOrdersQuery, { ...variables, after: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.customer?.orders?.pageInfo?.hasNextPage ? (lastPage.customer.orders.pageInfo.endCursor ?? undefined) : undefined,
  });

  const data = useMemo(() => mergeCaaOrderPages(ordersInfinite.data?.pages), [ordersInfinite.data]);

  const hasNextPage = useMemo(() => data?.customer?.orders?.pageInfo.hasNextPage || false, [data?.customer?.orders?.pageInfo]);

  /** 서버 최신 데이터로 목록을 갱신한다 (현재까지 로드된 페이지 전체 재조회) */
  const refetchOrders = () => ordersInfinite.refetch();

  return {
    data,
    loading: ordersInfinite.isFetching,
    fetchMore: () => ordersInfinite.fetchNextPage(),
    orders: data?.customer?.orders?.edges || [],
    hasNextPage,
    refetchOrders,
  };
}
